import type { SearchDetails } from 'models/SearchDetails';
import {
  findLayerFeaturesByDisplayLabels,
  getHighlightGraphics,
  isAssetsLayer,
  type QueryableAssetLayer,
} from 'utils/assetLayerSearch';

export interface LegendCheckedNode {
  readonly key?: string;
  readonly category?: string;
  readonly title?: string;
  readonly endPoint?: string;
  readonly checked?: boolean;
}

export interface AssetLayerSearchSyncUpdate {
  readonly searchContent: {
    readonly type: string;
    readonly selectedKey: string;
    readonly selectedLabel: string;
    readonly selectedCategory: string;
    readonly selectedEndPoint?: string;
    readonly selectedItem: undefined;
  };
  readonly selectedNode: LegendCheckedNode | null;
  readonly updateSelectedNode: boolean;
}

export type SearchResultsHighlightAction =
  | { readonly kind: 'clear' }
  | {
      readonly kind: 'highlight';
      readonly layerKey: string;
      readonly items: readonly string[];
      readonly autoSelectItem?: string;
    };

export function resolveAssetLayerSearchSync(
  checkedNodesState: readonly LegendCheckedNode[],
  selectedNode: LegendCheckedNode | null,
): AssetLayerSearchSyncUpdate {
  const latestByKey = new Map<string, LegendCheckedNode>();
  for (const node of checkedNodesState) {
    if (node?.key) {
      latestByKey.set(node.key, node);
    }
  }

  const isLayerChecked = (key: string): boolean => {
    const latest = latestByKey.get(key);
    return latest != null && latest.checked !== false;
  };

  const checkedNodes = [...latestByKey.values()].filter(
    (node) => node.checked !== false,
  );
  const checkedAssetNodes = checkedNodes.filter((node) =>
    isAssetsLayer(node.key ?? '', node.category, checkedNodesState),
  );

  if (checkedAssetNodes.length === 0) {
    return {
      searchContent: {
        type: 'network',
        selectedKey: 'network-layer',
        selectedLabel: 'Network',
        selectedCategory: 'Network',
        selectedItem: undefined,
      },
      selectedNode: null,
      updateSelectedNode: true,
    };
  }

  const hasMultipleLayers = checkedNodes.length > 1;
  const currentNodeStillChecked =
    selectedNode != null &&
    isLayerChecked(selectedNode.key ?? '') &&
    (isAssetsLayer(selectedNode.key ?? '', selectedNode.category, checkedNodesState) ||
      selectedNode.key === 'network-layer');

  const nodeToUse =
    hasMultipleLayers && currentNodeStillChecked
      ? selectedNode
      : checkedAssetNodes[0];

  if (nodeToUse == null) {
    return {
      searchContent: {
        type: 'network',
        selectedKey: 'network-layer',
        selectedLabel: 'Network',
        selectedCategory: 'Network',
        selectedItem: undefined,
      },
      selectedNode: null,
      updateSelectedNode: true,
    };
  }

  return {
    searchContent: {
      type: 'current-layer',
      selectedKey: nodeToUse.key ?? '',
      selectedLabel: nodeToUse.title ?? '',
      selectedCategory: nodeToUse.category ?? '',
      selectedEndPoint: nodeToUse.endPoint,
      selectedItem: undefined,
    },
    selectedNode: nodeToUse,
    updateSelectedNode: !currentNodeStillChecked,
  };
}

export function resolveSearchResultsHighlightAction(
  searchContent: Pick<SearchDetails, 'type' | 'selectedKey'>,
  items: readonly string[],
): SearchResultsHighlightAction {
  if (
    searchContent.type === 'current-layer' &&
    searchContent.selectedKey &&
    items.length > 0
  ) {
    return {
      kind: 'highlight',
      layerKey: searchContent.selectedKey,
      items,
      ...(items.length === 1 ? { autoSelectItem: items[0] } : {}),
    };
  }

  return { kind: 'clear' };
}

export async function applySearchResultHighlights(options: {
  readonly items: readonly string[];
  readonly layer: QueryableAssetLayer | null | undefined;
  readonly findFeaturesByLabels?: typeof findLayerFeaturesByDisplayLabels;
  readonly clearHighlight: () => void;
  readonly highlightGraphics: (
    layer: QueryableAssetLayer,
    graphics: readonly unknown[],
  ) => void | Promise<void>;
}): Promise<boolean> {
  if (!options.items.length) {
    options.clearHighlight();
    return false;
  }

  const layer = options.layer;
  if (!layer || typeof layer.queryFeatures !== 'function') {
    options.clearHighlight();
    return false;
  }

  const findFeatures = options.findFeaturesByLabels ?? findLayerFeaturesByDisplayLabels;

  try {
    const features = await findFeatures(layer, options.items, true);
    if (!features.length) {
      return false;
    }

    const graphics = getHighlightGraphics(features);
    if (graphics.length === 0) {
      return false;
    }

    await options.highlightGraphics(layer, graphics);
    return true;
  } catch {
    options.clearHighlight();
    return false;
  }
}
